/**
 * CT Product Detail — gallery, accordion, purchase mode, qty, wishlist
 */
(() => {
  const WISH_KEY = 'ct-wishlist-handles';

  const money = (cents, format) => {
    const amount = (Number(cents) || 0) / 100;
    const formatted = amount.toFixed(2);
    if (typeof format === 'string' && format.includes('{{amount}}')) {
      return format.replace(/\{\{\s*amount\s*\}\}/g, formatted);
    }
    return `$${formatted}`;
  };

  const readWish = () => {
    try {
      const raw = localStorage.getItem(WISH_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(list) ? list.map(String) : []);
    } catch {
      return new Set();
    }
  };

  const writeWish = (set) => {
    try {
      localStorage.setItem(WISH_KEY, JSON.stringify([...set]));
    } catch {
      /* private mode */
    }
  };

  class CtProductDetail {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.root = root;
      this.gallery = root.querySelector('[data-ct-pdp-gallery]');
      this.slides = [...root.querySelectorAll('[data-ct-pdp-slide]')];
      this.thumbs = [...root.querySelectorAll('[data-ct-pdp-thumb]')];
      this.purchase = root.querySelector('[data-ct-pdp-purchase]');
      this.index = Math.max(
        0,
        this.slides.findIndex((el) => el.classList.contains('is-active'))
      );
      this.wish = readWish();

      const planEl = root.querySelector('[data-ct-pdp-plans]');
      try {
        this.planData = planEl ? JSON.parse(planEl.textContent || '{}') : {};
      } catch {
        this.planData = {};
      }

      this.bind();
      this.syncWish();
      this.goTo(this.index, false);
    }

    bind() {
      this.root.querySelector('[data-ct-pdp-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
      this.root.querySelector('[data-ct-pdp-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const i = Number(thumb.dataset.index || 0);
          this.goTo(i);
        });
      });

      this.root.querySelectorAll('[data-ct-pdp-acc-trigger]').forEach((btn) => {
        btn.addEventListener('click', () => this.toggleAcc(btn));
      });

      this.root.querySelectorAll('[data-ct-pdp-mode]').forEach((btn) => {
        btn.addEventListener('click', () => this.setMode(btn));
      });

      const qtyInput = this.root.querySelector('[data-ct-pdp-qty-input]');
      this.root.querySelector('[data-ct-pdp-qty-minus]')?.addEventListener('click', () => {
        if (!(qtyInput instanceof HTMLInputElement)) return;
        const min = Number(qtyInput.min || 1);
        const step = Number(qtyInput.step || 1);
        qtyInput.value = String(Math.max(min, Number(qtyInput.value || min) - step));
      });
      this.root.querySelector('[data-ct-pdp-qty-plus]')?.addEventListener('click', () => {
        if (!(qtyInput instanceof HTMLInputElement)) return;
        const max = qtyInput.max ? Number(qtyInput.max) : Infinity;
        const step = Number(qtyInput.step || 1);
        qtyInput.value = String(Math.min(max, Number(qtyInput.value || 1) + step));
      });

      const variantSelect = this.root.querySelector('[data-ct-pdp-variant-select]');
      if (variantSelect instanceof HTMLSelectElement) {
        variantSelect.addEventListener('change', () => this.onVariantChange(variantSelect));
      }

      this.root.querySelector('[data-ct-pdp-wishlist]')?.addEventListener('click', (event) => {
        event.preventDefault();
        const btn = event.currentTarget;
        if (!(btn instanceof HTMLButtonElement)) return;
        this.toggleWish(btn.dataset.productHandle || '');
      });

      this.root.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const wishBtn = target.closest('[data-ct-pdp-pair-wishlist]');
        if (wishBtn instanceof HTMLButtonElement) {
          event.preventDefault();
          this.toggleWish(wishBtn.dataset.productHandle || '');
          return;
        }

        const addBtn = target.closest('[data-ct-pdp-pair-add]');
        if (addBtn instanceof HTMLButtonElement) {
          event.preventDefault();
          this.quickAdd(addBtn);
        }
      });
    }

    /** @param {string} handle */
    toggleWish(handle) {
      if (!handle) return;
      if (this.wish.has(handle)) this.wish.delete(handle);
      else this.wish.add(handle);
      writeWish(this.wish);
      this.syncWish();
      document.dispatchEvent(
        new CustomEvent('ct:wishlist-change', {
          bubbles: true,
          detail: { handle, active: this.wish.has(handle) },
        })
      );
    }

    /** @param {HTMLButtonElement} btn */
    async quickAdd(btn) {
      const variantId = btn.dataset.variantId;
      if (!variantId || btn.classList.contains('is-loading')) return;

      const label = btn.querySelector('[data-ct-pdp-pair-cta-label]');
      const original = label?.textContent?.trim() || 'Quick add';
      btn.classList.add('is-loading');
      btn.disabled = true;
      if (label) label.textContent = 'Adding…';

      try {
        const { CartLinesUpdateEvent } = await import('@shopify/events');

        const sectionIds = [
          ...new Set(
            [...document.querySelectorAll('cart-items-component')]
              .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
              .filter(Boolean)
          ),
        ];

        const formData = new FormData();
        formData.append('id', String(variantId));
        formData.append('quantity', '1');
        if (sectionIds.length) {
          formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', window.location.pathname);
        }

        const deferred = CartLinesUpdateEvent.createPromise();

        // Same event product-form uses — opens drawer, updates bubble, morphs cart HTML
        document.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: String(variantId), quantity: 1 }],
            promise: deferred.promise,
          })
        );

        const themeRoutes =
          (typeof Theme !== 'undefined' && Theme.routes) ||
          /** @type {{ routes?: { cart_add_url?: string, cart_url?: string } }} */ (window).Theme?.routes ||
          {};

        const addUrl =
          themeRoutes.cart_add_url ||
          `${window.Shopify?.routes?.root || '/'}cart/add.js`;

        const response = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        }).then((res) => res.json());

        // Shopify returns { status, message, description } on failure
        if (response.status) {
          const message = response.description || response.message || 'Add failed';
          deferred.reject(new Error(message));
          throw new Error(message);
        }

        const cartJsUrl = themeRoutes.cart_url
          ? `${themeRoutes.cart_url}.js`
          : `${window.Shopify?.routes?.root || '/'}cart.js`;

        const cart = await fetch(cartJsUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        }).then((res) => {
          if (!res.ok) throw new Error('Cart fetch failed');
          return res.json();
        });

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            itemCount: cart.item_count,
            sections: response.sections,
            source: 'ct-product-detail-pair',
            didError: false,
          },
        });

        if (label) label.textContent = 'Added';

        // Drawer opens via CartLinesUpdateEvent listener in ct-cart.js (once)

        window.setTimeout(() => {
          if (label) label.textContent = original;
          btn.disabled = false;
          btn.classList.remove('is-loading');
        }, 1200);
      } catch (error) {
        console.warn('[ct-pdp] pair quick add failed:', error);
        if (label) label.textContent = 'Try again';
        btn.disabled = false;
        btn.classList.remove('is-loading');
        window.setTimeout(() => {
          if (label) label.textContent = original;
        }, 1400);
      }
    }

    /** @param {number} next */
    goTo(next, animate = true) {
      if (!this.slides.length) return;
      const total = this.slides.length;
      this.index = ((next % total) + total) % total;

      this.slides.forEach((slide, i) => {
        const on = i === this.index;
        slide.classList.toggle('is-active', on);
        slide.hidden = !on;
        if (animate) slide.classList.toggle('is-animating', on);
      });

      this.thumbs.forEach((thumb, i) => {
        const on = i === this.index;
        thumb.classList.toggle('is-active', on);
        thumb.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) thumb.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      });
    }

    /** @param {HTMLElement} btn */
    toggleAcc(btn) {
      const item = btn.closest('[data-ct-pdp-acc-item]');
      const panel = item?.querySelector('[data-ct-pdp-acc-panel]');
      if (!(item instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

      const open = !item.classList.contains('is-open');
      item.classList.toggle('is-open', open);
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');

      if (open) {
        panel.hidden = false;
        panel.style.height = '0px';
        requestAnimationFrame(() => {
          panel.style.height = `${panel.scrollHeight}px`;
        });
        const done = () => {
          panel.style.height = 'auto';
          panel.removeEventListener('transitionend', done);
        };
        panel.addEventListener('transitionend', done);
      } else {
        panel.style.height = `${panel.scrollHeight}px`;
        requestAnimationFrame(() => {
          panel.style.height = '0px';
        });
        const done = () => {
          panel.hidden = true;
          panel.style.height = '';
          panel.removeEventListener('transitionend', done);
        };
        panel.addEventListener('transitionend', done);
      }
    }

    /** @param {HTMLElement} btn */
    setMode(btn) {
      const mode = btn.dataset.ctPdpMode || 'onetime';
      const planId = btn.dataset.sellingPlan || '';
      this.purchase?.setAttribute('data-mode', mode);

      this.root.querySelectorAll('[data-ct-pdp-mode]').forEach((el) => {
        const on = el === btn;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-checked', on ? 'true' : 'false');
      });

      const planInput = this.root.querySelector('[data-ct-pdp-selling-plan]');
      if (planInput instanceof HTMLInputElement) {
        if (mode === 'subscription' && planId) {
          planInput.disabled = false;
          planInput.value = planId;
        } else {
          planInput.value = '';
          planInput.disabled = true;
        }
      }
    }

    /** @param {HTMLSelectElement} select */
    onVariantChange(select) {
      const id = select.value;
      const variantInput = this.root.querySelector('[data-ct-pdp-variant-id]');
      if (variantInput instanceof HTMLInputElement) variantInput.value = id;

      const variant = (this.planData.variants || []).find((v) => String(v.id) === String(id));
      if (!variant) return;

      const oneEl = this.root.querySelector('[data-ct-pdp-one-price]');
      const subEl = this.root.querySelector('[data-ct-pdp-sub-price]');
      const compareEl = this.root.querySelector('[data-ct-pdp-sub-compare]');
      const format = this.planData.moneyFormat;

      if (oneEl) oneEl.textContent = money(variant.price, format);

      const planId = this.planData.firstPlanId;
      const subPrice = planId && variant.plans?.[planId] != null ? variant.plans[planId] : variant.price;
      if (subEl) subEl.textContent = money(subPrice, format);
      if (compareEl) {
        compareEl.textContent = money(variant.price, format);
        compareEl.hidden = !(variant.price > subPrice);
      }

      const cta = this.root.querySelector('[data-ct-pdp-cta-label]');
      const submit = this.root.querySelector('.ct-pdp__cta');
      if (submit instanceof HTMLButtonElement) {
        submit.disabled = !variant.available;
      }
      if (cta && !variant.available) {
        cta.textContent = this.root.dataset.soldOutLabel || 'Sold out';
      }
    }

    syncWish() {
      this.root.querySelectorAll('[data-ct-pdp-wishlist], [data-ct-pdp-pair-wishlist]').forEach((btn) => {
        if (!(btn instanceof HTMLButtonElement)) return;
        const handle = btn.dataset.productHandle || '';
        const on = handle !== '' && this.wish.has(handle);
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
  }

  const init = () => {
    document.querySelectorAll('[data-ct-pdp]').forEach((el) => {
      if (!(el instanceof HTMLElement) || el.dataset.ctReady === 'true') return;
      el.dataset.ctReady = 'true';
      new CtProductDetail(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
